import { create } from 'zustand';
import {
  PreOrderSchema,
  PreOrderRequest,
  UpdatePreOrderRequest,
  PreOrderQueryParams,
  PreOrderPagination,
  UpdatePreOrderStatusRequest,
} from '../types/pre-order.type';
import * as preOrderService from '../services/pre-order.service';

interface PreOrderState {
  // Pre-Orders
  preOrders: PreOrderSchema[];
  preOrdersLoading: boolean;
  preOrdersError: string | null;
  totalPreOrders: number;
  preOrdersPage: number;
  preOrdersHasMore: boolean;
  preOrdersTotalPages: number;
  lastQueryParams: PreOrderQueryParams | null;

  // Selected
  selectedPreOrder: PreOrderSchema | null;
  selectedPreOrderLoading: boolean;
  selectedPreOrderError: string | null;

  // Actions - Pre-Orders
  fetchPreOrders: (params: PreOrderQueryParams) => Promise<void>;
  fetchPreOrder: (id: string) => Promise<void>;
  createPreOrder: (data: PreOrderRequest) => Promise<void>;
  updatePreOrder: (id: string, data: UpdatePreOrderRequest) => Promise<boolean>;
  updatePreOrderStatus: (id: string, data: UpdatePreOrderStatusRequest) => Promise<void>;
  deletePreOrder: (id: string) => Promise<void>;
  setSelectedPreOrder: (item: PreOrderSchema | null) => void;
  clearPreOrdersError: () => void;
  clearSelectedPreOrderError: () => void;
}

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

export const usePreOrderStore = create<PreOrderState>((set, get) => ({
  // Pre-Orders
  preOrders: [],
  preOrdersLoading: false,
  preOrdersError: null,
  totalPreOrders: 0,
  preOrdersPage: 1,
  preOrdersHasMore: false,
  preOrdersTotalPages: 0,
  lastQueryParams: null,

  // Selected
  selectedPreOrder: null,
  selectedPreOrderLoading: false,
  selectedPreOrderError: null,

  // Actions - Pre-Orders
  fetchPreOrders: async (params) => {
    try {
      set({ preOrdersLoading: true, preOrdersError: null });
      const response = await preOrderService.getPreOrders(params);
      const data = response as GenericResponse<PreOrderPagination>;
      set({
        preOrders: data.result.pre_orders,
        totalPreOrders: data.result.total,
        preOrdersPage: data.result.page,
        preOrdersHasMore: data.result.page < Math.ceil(data.result.total / data.result.limit),
        preOrdersTotalPages: Math.ceil(data.result.total / data.result.limit),
        lastQueryParams: params,
        preOrdersLoading: false,
      });
    } catch (error) {
      set({
        preOrdersError: error instanceof Error ? error.message : 'Failed to fetch pre-orders',
        preOrdersLoading: false,
      });
    }
  },
  fetchPreOrder: async (id) => {
    try {
      set({ selectedPreOrderLoading: true, selectedPreOrderError: null });
      const response = await preOrderService.getPreOrder(id);
      const data = response as GenericResponse<PreOrderSchema>;
      set({ selectedPreOrder: data.result, selectedPreOrderLoading: false });
    } catch (error) {
      set({
        selectedPreOrderError: error instanceof Error ? error.message : 'Failed to fetch pre-order',
        selectedPreOrderLoading: false,
      });
    }
  },
  createPreOrder: async (data) => {
    try {
      set({ preOrdersLoading: true, preOrdersError: null });
      await preOrderService.createPreOrder(data);
      const currentParams = get().lastQueryParams ?? {
        organization_id: data.organization_id,
        page: 1,
        limit: 20,
      };
      await get().fetchPreOrders(currentParams);
    } catch (error) {
      set({
        preOrdersError: error instanceof Error ? error.message : 'Failed to create pre-order',
        preOrdersLoading: false,
      });
    }
  },
  updatePreOrder: async (id, data) => {
    try {
      set({ preOrdersLoading: true, preOrdersError: null });
      await preOrderService.updatePreOrder(id, data);
      const currentParams = get().lastQueryParams;
      if (currentParams?.organization_id) {
        await get().fetchPreOrders(currentParams);
      } else {
        set({ preOrdersLoading: false });
      }
      if (get().selectedPreOrder?.id === id) {
        await get().fetchPreOrder(id);
      }
      return true;
    } catch (error) {
      set({
        preOrdersError: error instanceof Error ? error.message : 'Failed to update pre-order',
        preOrdersLoading: false,
      });
      return false;
    }
  },
  updatePreOrderStatus: async (id, data) => {
    try {
      set({ preOrdersLoading: true, preOrdersError: null });
      await preOrderService.updatePreOrderStatus(id, data);
      const currentParams = get().lastQueryParams;
      if (currentParams?.organization_id) {
        await get().fetchPreOrders(currentParams);
      } else {
        set({ preOrdersLoading: false });
      }
      if (get().selectedPreOrder?.id === id) {
        await get().fetchPreOrder(id);
      }
    } catch (error) {
      set({
        preOrdersError: error instanceof Error ? error.message : 'Failed to update pre-order status',
        preOrdersLoading: false,
      });
    }
  },
  deletePreOrder: async (id) => {
    try {
      set({ preOrdersLoading: true, preOrdersError: null });
      await preOrderService.deletePreOrder(id);
      const currentParams = get().lastQueryParams;
      if (currentParams?.organization_id) {
        await get().fetchPreOrders(currentParams);
      } else {
        set({ preOrdersLoading: false });
      }
      if (get().selectedPreOrder?.id === id) {
        set({ selectedPreOrder: null });
      }
    } catch (error) {
      set({
        preOrdersError: error instanceof Error ? error.message : 'Failed to delete pre-order',
        preOrdersLoading: false,
      });
    }
  },
  setSelectedPreOrder: (item) => {
    set({ selectedPreOrder: item });
  },
  clearPreOrdersError: () => {
    set({ preOrdersError: null });
  },
  clearSelectedPreOrderError: () => {
    set({ selectedPreOrderError: null });
  },
})); 
